import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { BookCheck } from 'lucide-react';
import { PageShell } from '@/components/common/PageShell';
import { Badge, Button, Card, Spinner } from '@/components/ui';
import { listMyPolicies, type MyPolicy } from '@/api/services/hrPolicies.service';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { formatLetterDate } from '../letters/letterFormat';
import { ASSIGNMENT_STATUS } from '../policies/policyFormat';

function PolicyRow({ policy }: { policy: MyPolicy }): React.ReactElement {
  const navigate = useNavigate();
  const status = policy.overdue
    ? { label: 'Overdue', variant: 'danger' as const }
    : ASSIGNMENT_STATUS[policy.status];
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
          {policy.title}
        </p>
        <p className="text-xs text-gray-600 dark:text-gray-300">
          Version {policy.version}
          {policy.awaitingMe && policy.dueAt
            ? ` · acknowledge by ${formatLetterDate(policy.dueAt)}`
            : policy.acknowledgedAt
              ? ` · acknowledged ${formatLetterDate(policy.acknowledgedAt)}`
              : ` · effective ${formatLetterDate(policy.effectiveAt)}`}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Badge variant={status.variant}>{status.label}</Badge>
        <Button
          size="sm"
          variant={policy.awaitingMe ? 'primary' : 'secondary'}
          onClick={() => navigate(`/my-policies/${policy.assignmentId}`)}
        >
          {policy.awaitingMe ? 'Read and acknowledge' : 'Read'}{' '}
          <span className="sr-only">{policy.title}</span>
        </Button>
      </div>
    </li>
  );
}

/**
 * The signed-in user's policies: those waiting for acknowledgement first,
 * then the policies that apply to them and their acknowledgement history.
 */
export function MyPoliciesPage(): React.ReactElement {
  const { data, isLoading, error } = useQuery({
    queryKey: ['my-policies'],
    queryFn: listMyPolicies,
  });
  const policies = Array.isArray(data) ? data : [];
  const waiting = policies.filter((policy) => policy.awaitingMe);
  const current = policies.filter(
    (policy) => !policy.awaitingMe && policy.versionStatus === 'published'
  );
  const history = policies.filter((policy) => policy.versionStatus !== 'published');

  let body: React.ReactNode;
  if (isLoading) {
    body = (
      <Card>
        <div className="flex justify-center py-12" role="status">
          <Spinner />
          <span className="sr-only">Loading policies</span>
        </div>
      </Card>
    );
  } else if (error) {
    body = (
      <Card>
        <p className="text-sm text-danger" role="alert">
          {getErrorMessage(error)}
        </p>
      </Card>
    );
  } else if (policies.length === 0) {
    body = (
      <Card>
        <div className="py-10 text-center">
          <BookCheck className="mx-auto h-8 w-8 text-gray-400" aria-hidden />
          <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
            No policies yet. Policies and handbooks that apply to you appear here.
          </p>
        </div>
      </Card>
    );
  } else {
    const section = (title: string, rows: MyPolicy[]) =>
      rows.length > 0 && (
        <Card
          header={
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
          }
          contentClassName="p-0"
        >
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {rows.map((policy) => (
              <PolicyRow key={policy.assignmentId} policy={policy} />
            ))}
          </ul>
        </Card>
      );
    body = (
      <div className="space-y-6">
        {section('Waiting for your acknowledgement', waiting)}
        {section('Policies that apply to you', current)}
        {section('Earlier versions you acknowledged', history)}
      </div>
    );
  }

  return (
    <PageShell
      breadcrumbItems={[{ label: 'Self Service', to: '/dashboard' }, { label: 'My Policies' }]}
      icon={BookCheck}
      title="My Policies"
      description="Policies and handbooks that apply to you, and those waiting for your acknowledgement."
      className="gap-0"
      hideHeader
    >
      <div className="mx-auto w-full max-w-4xl">{body}</div>
    </PageShell>
  );
}
